import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { ADMIN_RESET_CODE } from '../config/constants';
import { Button, Input, Card } from '../components/UI';
import Logo from '../components/Logo';

const AdminReset = () => {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate all fields
    if (!email || !resetCode || !newPassword || !confirmPassword) {
      setError('Todos los campos son requeridos');
      setLoading(false);
      return;
    }

    // Validate reset code
    if (resetCode !== ADMIN_RESET_CODE) {
      setError('Código de reseteo inválido');
      setLoading(false);
      return;
    }

    // Validate password match
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }

    // Attempt password reset
    const result = resetPassword(email, resetCode, newPassword);
    
    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        navigate('/liberado-jefe-login');
      }, 2000);
    } else {
      setError(result.error || 'Error al resetear contraseña');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 px-4">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold mb-2 text-green-600">¡Contraseña Actualizada!</h1>
            <p className="text-gray-600 mb-4">Tu contraseña ha sido cambiada exitosamente.</p>
            <p className="text-sm text-gray-500">Redirigiendo al login...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 px-4">
      {/* Header with Logo and Language */}
      <div className="absolute top-0 left-0 right-0 py-6 px-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Logo size="medium" />
            <span className="text-xl font-bold text-white">LIBERADO</span>
          </div>
          
          {/* Language Selector */}
          <div className="flex items-center space-x-1 bg-purple-800 rounded-lg px-2 py-1">
            {['es', 'en', 'pt'].map((lang) => (
              <button
                key={lang}
                onClick={() => changeLanguage(lang)}
                className={`px-2 py-1 rounded text-sm font-medium transition ${
                  language === lang
                    ? 'bg-yellow-500 text-black'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Card className="max-w-md w-full mt-20">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">🔑</div>
          <h1 className="text-3xl font-bold mb-2">Resetear Contraseña</h1>
          <p className="text-gray-600">Ingresa el código de reseteo para cambiar tu contraseña</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Input
            label="Email de Administrador"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            required
            autoComplete="email"
          />

          <Input
            label="Código de Reseteo Secreto"
            type="text"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            placeholder="LIBERADO-RESET-XXXX"
            required
            autoComplete="off"
          />

          <Input
            label="Nueva Contraseña"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />

          <Input
            label="Confirmar Nueva Contraseña"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
            disabled={loading}
          >
            {loading ? 'Procesando...' : 'Resetear Contraseña'}
          </Button>
        </form>

        <div className="text-center mt-4">
          <Link 
            to="/liberado-jefe-login" 
            className="text-sm text-purple-600 hover:text-purple-800 underline"
          >
            Volver al Login
          </Link>
        </div>

        <p className="text-center mt-4 text-sm text-gray-500">
          Solo acceso autorizado
        </p>
      </Card>
    </div>
  );
};

export default AdminReset;
